/**
 * Russian is the source locale: it is written first and `en.ts` is typed against
 * it, so a missing or renamed translation is a compile error rather than a
 * string that quietly falls back at runtime.
 *
 * Strings are nested and consumed as `t.wallet.deposit.title` — that gives full
 * autocomplete and makes a typo impossible, which `t('wallet.deposit.title')`
 * never does.
 *
 * Numerals are never interpolated raw: a string that mentions a count takes a
 * `Count` from `lib/format`, so the figure arrives already grouped (12 400, not
 * 12400) and the plural form agrees with it (1 подписчик, 2 подписчика,
 * 5 подписчиков). See DESIGN_SYSTEM.md section 4.
 */
import { pluralRu, type Count } from '../lib/format'

export const ru = {
  common: {
    /** The amount field's fill-to-maximum button (Withdraw, Transfer) — nothing else. */
    all: 'Макс.',
    back: 'Назад',
    cancel: 'Отмена',
    confirm: 'Подтвердить',
    copy: 'Копировать',
    copied: 'Скопировано',
    open: 'Открыть',
    retry: 'Повторить',
    save: 'Сохранить',
    close: 'Закрыть',
    loading: 'Загрузка',
    available: 'Доступно',
    total: 'Всего',
    amount: 'Сумма',
    network: 'Сеть',
    asset: 'Валюта',
    status: 'Статус',
    date: 'Дата',
    nothingYet: 'Пока пусто',
    somethingWentWrong: 'Что-то пошло не так',
    reload: 'Перезагрузить',
    crashHint: 'Экран не удалось показать. Перезагрузите приложение — деньги и заявки не затронуты.',
  },

  nav: {
    label: 'Основная навигация',
    home: 'Главная',
    markets: 'Рынки',
    signals: 'Сигналы',
    trade: 'Торговля',
    wallet: 'Активы',
  },

  auth: {
    signIn: 'Вход',
    signInSubtitle: 'Войдите, чтобы вернуться к сигналам и балансу',
    signInAction: 'Войти',
    signUp: 'Регистрация',
    signUpSubtitle: 'Почта и пароль — этого достаточно, чтобы начать',
    signUpSubtitleInvite: 'Почта, пароль и код приглашения — этого достаточно, чтобы начать',
    email: 'Электронная почта',
    emailPlaceholder: 'name@example.com',
    password: 'Пароль',
    passwordHint: 'Минимум 8 символов, буквы и цифры',
    passwordConfirm: 'Повторите пароль',
    referralCode: 'Код приглашения',
    referralCodePlaceholder: 'Например, 8K3M7QPL',
    referralCodeHint: 'Необязательно — если вас пригласил участник 19CRT',
    referralCodeRequiredHint: 'Регистрация только по приглашению: используйте ссылку или код того, кто вас пригласил',
    /**
     * The programme's live terms under an invitation code: what the newcomer
     * gets and for what. Only a member's personal code carries them — an
     * operator's invitation code has no referrer and no perks, and the form
     * cannot tell the two apart — so the promise is conditional.
     */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0)
          ? `${signals.text} ${pluralRu(signals, ['бонусный сигнал', 'бонусных сигнала', 'бонусных сигналов'])}`
          : null,
        promo === null ? null : `промо-бонус ${promo}\u00A0USDT`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `Если код вам дал участник 19CRT, приглашение засчитается после первого депозита от ${deposit}\u00A0${asset}`
        : `Если код вам дал участник 19CRT, после первого депозита от ${deposit}\u00A0${asset} вы получите ${perks.join(' и ')}`
    },
    forgot: 'Забыли пароль?',
    createAccount: 'Создать аккаунт',
    haveAccount: 'Уже есть аккаунт?',
    noAccount: 'Нет аккаунта?',
    backToSignIn: 'Вернуться ко входу',
    verifyTitle: 'Подтвердите почту',
    verifySent: (email: string) => `Письмо отправлено на ${email}`,
    verifySentAnywhere: 'Письмо со ссылкой отправлено на вашу почту',
    verifyHint:
      'Откройте ссылку из письма, чтобы подтвердить адрес: на него приходят письма для восстановления доступа. Если письма нет, проверьте папку «Спам».',
    verifyEmailPrompt: 'Укажите почту, на которую зарегистрирован аккаунт',
    verifyResend: 'Отправить письмо ещё раз',
    verifyResendIn: (seconds: Count) => `Отправить ещё раз можно через ${seconds.text} с`,
    verifyResent: 'Отправили ещё одно письмо',
    verifyResentMaybe: 'Если адрес зарегистрирован и ещё не подтверждён, новое письмо уже в пути',
    verifyChecking: 'Проверяем ссылку',
    verifyDone: 'Почта подтверждена',
    verifyDoneHint: 'Открываем приложение',
    verifyDoneSignInHint: 'Открываем вход',
    resetTitle: 'Восстановление пароля',
    resetSubtitle: 'Укажите почту — пришлём ссылку для смены пароля',
    resetSend: 'Отправить ссылку',
    resetSent: 'Если такой аккаунт есть, письмо уже отправлено',
    resetSentHint:
      'Мы не показываем, зарегистрирован ли адрес: иначе по этой форме можно было бы проверять чужие почты. Загляните во «Входящие» и в «Спам».',
    newPassword: 'Новый пароль',
    newPasswordSubtitle: 'Придумайте новый пароль. Старый перестанет работать сразу.',
    newPasswordSubmit: 'Сохранить пароль',
    requestNewLink: 'Запросить новую ссылку',
    newPasswordDone: 'Пароль обновлён',
    newPasswordDoneHint: 'Открываем вход',
    signOut: 'Выйти',
    totpCode: 'Код подтверждения',
    totpHint: 'Шесть цифр из приложения-аутентификатора',
    recoveryCode: 'Код восстановления',
    recoveryCodeHint: 'Восемь символов из кодов, сохранённых при включении 2FA',
    useRecoveryCode: 'Войти с кодом восстановления',
    useAppCode: 'Ввести код из приложения',
    resetLinkInvalid: 'Ссылка не работает',
    errors: {
      invalidCredentials: 'Неверная почта или пароль',
      emailTaken: 'Такая почта уже зарегистрирована',
      weakPassword: 'Пароль слишком короткий',
      passwordRule: 'Пароль должен содержать буквы и цифры',
      passwordsDiffer: 'Пароли не совпадают',
      invalidEmail: 'Введите адрес в формате name@example.com',
      invalidReferralCode: 'Проверьте код приглашения',
      referralRequired: 'Введите код приглашения',
      emailRequired: 'Укажите почту',
      passwordRequired: 'Введите пароль',
      accountBlocked: 'Доступ к аккаунту ограничен. Напишите в поддержку',
      rateLimited: 'Слишком много попыток. Попробуйте позже',
      totpRequired: 'Введите код из приложения-аутентификатора',
      totpInvalid: 'Код не подошёл. Проверьте время на телефоне и введите новый',
      totpFormat: 'Код из приложения — шесть цифр, код восстановления — восемь символов',
      expiredToken: 'Ссылка устарела. Запросите новую',
      verifyLinkUsed: 'Ссылка уже использована. Войдите — или откройте самое свежее письмо, если запрашивали новое',
      missingToken: 'Ссылка неполная. Откройте её из письма целиком',
      validation: 'Проверьте правильность заполненных полей',
      network: 'Нет связи с сервером. Проверьте подключение',
    },
  },

  home: {
    promoLabel: 'Промо',
    banners: [
      {
        title: 'Сигналы по золоту',
        subtitle: 'Код из нашего Telegram-канала\u00A0— сделка в один шаг',
        cta: 'Ввести код',
      },
      {
        title: 'Рынки в реальном времени',
        subtitle: 'Живые графики и стакан по всем парам',
        cta: 'Открыть рынки',
      },
      {
        title: 'Пополняйте за минуты',
        subtitle: 'USDT в сетях TRC20 и ERC20',
        cta: 'Пополнить счёт',
      },
    ],
    pauseAnnouncements: 'Приостановить объявления',
    resumeAnnouncements: 'Продолжить объявления',
    announcementsLabel: 'Объявления',
    announcements: [
      'Перед пополнением проверьте валюту и сеть перевода',
      'Управляйте защитой аккаунта в разделе «Безопасность»',
      'История операций доступна в разделе «Активы»',
    ],
    totalBalance: 'Общий баланс',
    quickTrade: 'Быстрая торговля',
    quickTradeHint: 'Спот и фьючерсы в одном терминале',
    quickActions: 'Быстрые действия',
    signalLive: 'Сигнал доступен',
    signalNext: 'Следующий сигнал',
    signalClosesIn: 'закроется через',
    signalOpensAt: (time: string) => `откроется в ${time}`,
    signalsTitle: 'Сигналы по золоту',
    allSignals: 'Все сигналы',
    marketMoves: 'Движение рынка',
    pair: 'Пара',
    lastPrice: 'Цена',
    change24h: 'Изменение 24ч',
    /** After the figure in the home balance's change pill: « +1.30% за 24ч ». */
    change24hShort: 'за 24ч',
    volume24h: 'Объём 24ч',
    allMarkets: 'Все рынки',
    pricesUnavailable: 'Курсы недоступны — не можем оценить весь портфель',
    ratesUnavailable: 'Курсы временно недоступны',
    marketsUnavailable: 'Список рынков временно недоступен',
    signalsNone: 'Сейчас сигналов нет\u00A0— новые коды выходят в нашем Telegram-канале',
  },

  markets: {
    title: 'Рынки',
    search: 'Поиск пары',
    spot: 'Спот',
    futures: 'Фьючерсы',
    gainers: 'Топ роста',
    losers: 'Снижения',
    volume: 'Объём 24ч',
    volumeShort: 'объём',
    typeLabel: 'Тип рынка',
    sortLabel: 'Сортировка',
    clearSearch: 'Очистить поиск',
    favourites: 'Избранное',
    addFavourite: 'В избранное',
    removeFavourite: 'Убрать из избранного',
    emptyFavourites: 'В избранном пока пусто — отметьте пары звёздочкой',
    empty: 'Ничего не найдено',
    emptyHint: 'Проверьте символ пары — например, BTC или SOL.',
    emptySpot: 'Спотовые пары пока не открыты',
    emptyFutures: 'Фьючерсные пары пока не открыты',
    loadFailed: 'Не удалось загрузить список рынков',
    pricesUnavailable: 'Котировки временно недоступны. Цены обновятся автоматически.',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: 'Золото' } as Record<string, string>,
  },

  wallet: {
    title: 'Активы',
    totalValue: 'Общая стоимость счёта',
    accounts: 'Мои счета',
    latestWithdrawal: 'Последний вывод',
    coin: 'Монета',
    searchCoin: 'Поиск монеты',
    quickActions: 'Быстрые действия',
    coins: 'Монеты',
    hideBalance: 'Скрыть баланс',
    showBalance: 'Показать баланс',
    loadError: 'Не удалось загрузить баланс',
    pricesUnavailable: 'Курсы недоступны — оценить весь портфель нельзя',
    coinsEmpty: 'Монет пока нет. Пополните счёт — они появятся здесь.',
    accountSpot: 'Спотовый счёт',
    accountSpotHint: 'Доступно для торговли',
    accountFutures: 'Фьючерсный счёт',
    accountFuturesHint: 'Маржа под позиции',
    accountCopy: 'Копирование (архив)',
    accountCopyHint: 'Переведите на спот',
    actions: {
      deposit: 'Пополнить',
      withdraw: 'Вывести',
      transfer: 'Перевод',
      history: 'История',
    },
    deposit: {
      title: 'Пополнение',
      history: 'История пополнений',
      chain: 'Сеть',
      saveQr: 'Сохранить QR-код',
      address: 'Адрес пополнения',
      copyAddress: 'Копировать адрес',
      notice: 'Важное уведомление',
      noticeWrongNetwork: (asset: string, network: string) =>
        `Отправляйте только ${asset} в сети ${network}. Монеты из другой сети будут потеряны.`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        // "после" and "около" both govern the genitive: 1 takes the singular
        // genitive (подтверждения, минуты), every other count the plural one.
        `Зачисление после ${count.text} ${pluralRu(count, ['подтверждения', 'подтверждений', 'подтверждений'])} сети, обычно около ${minutes.text} ${pluralRu(minutes, ['минуты', 'минут', 'минут'])}.`,
      noticeMinimum: (amount: string, asset: string) =>
        `Минимальная сумма пополнения — ${amount} ${asset}.`,
      waiting: 'Ожидаем перевод',
      incoming: 'Поступления в пути',
      incomingFirst: 'Перевод замечен в сети, ждём первое подтверждение',
      incomingProgress: (done: Count, total: Count) => `Подтверждений: ${done.text} из ${total.text}`,
      selectAsset: 'Выберите валюту',
      noAssets: 'Пополнение временно недоступно. Попробуйте позже.',
      noNetworks: 'Для этой монеты пока нет доступных сетей — выберите другую.',
      addressFailed: 'Не удалось получить адрес пополнения',
      noticePermanent: 'Адрес постоянный: используйте его для всех следующих пополнений.',
      qrSaved: 'QR-код сохранён',
      qrFailed: 'Не удалось сохранить QR-код',
      paymentLink: 'Ссылка для кошелька',
      paymentLinkHint: 'Откройте её в кошельке — сеть и адрес подставятся сами.',
      copyLink: 'Копировать ссылку',
    },
    withdraw: {
      title: 'Вывод',
      history: 'История выводов',
      asset: 'Валюта вывода',
      selectAsset: 'Выберите валюту',
      network: 'Сеть вывода',
      address: 'Адрес вывода',
      addressPlaceholder: (network: string) => `Введите адрес ${network}`,
      addressPaste: 'Вставить адрес',
      addressFromBook: (label: string): string => `Подтверждён в адресной книге · ${label}`,
      paste: 'Вставить',
      addressInvalid: (network: string) => `Не похоже на адрес сети ${network}`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `Это адрес сети ${detected}, а выбрана ${selected}`,
      amount: 'Сумма вывода',
      amountPlaceholder: '0.00',
      networkFee: 'Комиссия сети',
      platformFee: (rate: string): string => `Комиссия платформы (${rate}%)`,
      feeFree: 'сейчас без комиссии',
      feeDynamic: 'по цене газа сейчас',
      willReceive: 'К получению',
      notice: 'Важное уведомление',
      noticeReview: 'Каждая заявка проходит проверку перед отправкой. Средства резервируются сразу.',
      noticeTiming: 'Отправка в течение 24 часов после одобрения.',
      noticeMinimum: (amount: string, asset: string) =>
        `Минимальная сумма вывода — ${amount} ${asset}.`,
      noticeSupport:
        'Проверьте адрес сами: перевод на чужой адрес вернуть невозможно. Спорные заявки поддержка разбирает по номеру заявки.',
      insufficient: 'Недостаточно средств',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `Осталось сегодня: ${remaining} из ${limit} USDT.` : `Без верификации — до ${limit} USDT в сутки, осталось ${remaining}. Пройдите верификацию, чтобы поднять лимит.`,
      addressNotConfirmed: 'Выводы только на адреса из адресной книги, подтверждённые по почте',
      limitReached: (verified: boolean): string => (verified ? 'Суточный лимит вывода исчерпан' : 'Суточный лимит без верификации исчерпан — пройдите верификацию в профиле'),
      belowMinimum: (amount: string, asset: string) => `Минимум ${amount} ${asset}`,
      confirmTitle: 'Проверьте заявку',
      confirmSubtitle: 'Отменить вывод после отправки нельзя. Проверьте сеть и адрес.',
      confirmAction: 'Подтвердить вывод',
      failed: 'Не удалось создать заявку',
      submitted: 'Заявка создана',
      submittedHint: 'После проверки средства уйдут в сеть. Статус заявки — в списке заявок на вывод.',
      requestId: 'Номер заявки',
      statusPending: 'На проверке',
      toRequests: 'К заявкам на вывод',
      empty: 'Сейчас нет валют, доступных для вывода',
      loadFailed: 'Не удалось загрузить список валют',
      routeUnavailable: 'Вывод в этой сети сейчас недоступен — выберите другую сеть',
      invalidAmount: 'Сумма не подходит: проверьте точность и комиссию сети',
      edit: 'Исправить заявку',
    },
    withdrawals: {
      title: 'Заявки на вывод',
      toAddress: (address: string): string => `на ${address}`,
      note: 'Отклонённые и неудавшиеся заявки возвращают резерв на счёт. Откройте заявку, чтобы увидеть транзакцию или причину.',
      empty: 'Заявок на вывод пока нет. Здесь будет статус каждой заявки — от проверки до подтверждения сетью.',
      loadError: 'Не удалось загрузить заявки',
      details: 'Заявка на вывод',
      created: 'Создана',
      transaction: 'Транзакция',
      openExplorer: 'Открыть в обозревателе',
      reason: 'Причина',
      status: {
        pending: 'На проверке',
        approved: 'Одобрена',
        rejected: 'Отклонена',
        broadcasting: 'Отправляется',
        sent: 'Отправлена',
        confirmed: 'Подтверждена',
        failed: 'Не удалась',
      },
      statusHint: {
        pending: 'Заявка проходит проверку. Сумма зарезервирована на счёте.',
        approved: 'Проверка пройдена, заявка ожидает отправки в сеть.',
        rejected: 'Заявка отклонена, резерв возвращён на счёт.',
        broadcasting: 'Транзакция отправляется в сеть.',
        sent: 'Транзакция в сети, ждём подтверждения.',
        confirmed: 'Сеть подтвердила транзакцию. Средства доставлены.',
        failed: 'Отправить не удалось, резерв возвращён на счёт.',
      },
    },
    transfer: {
      title: 'Перевод',
      from: 'Откуда',
      fromAvailable: (amount: string): string => `Откуда · доступно ${amount}`,
      to: 'Куда',
      submit: 'Перевести',
      swap: 'Поменять счета местами',
      selectFrom: 'Счёт списания',
      selectTo: 'Счёт зачисления',
      selectAsset: 'Выберите монету',
      after: 'После перевода',
      insufficient: 'На счёте списания недостаточно средств',
      done: 'Перевод выполнен',
      hint: 'Переводы между своими счетами мгновенные и без комиссии.',
      empty: 'Переводить пока нечего — сначала пополните счёт.',
      failed: 'Не удалось выполнить перевод. Попробуйте ещё раз',
    },
    history: {
      title: 'История операций',
      deposit: 'Пополнение',
      withdrawal: 'Вывод',
      trade: 'Сделка',
      fee: 'Комиссия',
      transfer: 'Перевод',
      pnl: 'Результат',
      empty: 'Операций пока нет. Здесь появятся пополнения, выводы и сделки.',
      emptyFiltered: 'По этому фильтру операций нет',
      loadError: 'Не удалось загрузить историю',
      today: 'Сегодня',
      yesterday: 'Вчера',
      balanceAfter: (amount: string) => `остаток ${amount}`,
      onlyAccount: (account: string) => `Только ${account}`,
      resetAccount: 'Показать все счета',
      accountFilter: 'Счёт',
      allAccounts: 'Все счета',
      withdrawalsLink: 'Статусы заявок на вывод',
      badges: {
        credited: 'Зачислено',
        sent: 'Отправлено',
        fee: 'Комиссия',
        reserved: 'В резерве',
        returned: 'Возвращено',
        released: 'Освобождено',
        filled: 'Исполнено',
        settled: 'Рассчитано',
        liquidated: 'Ликвидация',
        done: 'Выполнено',
        adjusted: 'Корректировка',
        paid: 'Выплачено',
      },
      filters: {
        all: 'Все',
        rewards: 'Бонусы',
        deposits: 'Пополнения',
        withdrawals: 'Выводы',
        trades: 'Сделки',
        transfers: 'Переводы',
      },
      types: {
        deposit: 'Пополнение',
        withdrawal: 'Вывод',
        withdrawal_fee: 'Комиссия за вывод',
        withdrawal_platform_fee: 'Комиссия платформы за вывод',
        withdrawal_lock: 'Резерв под вывод',
        withdrawal_release: 'Возврат резерва вывода',
        order_lock: 'Резерв под лимитный ордер',
        order_release: 'Возврат резерва ордера',
        bonus: 'Промо-бонус',
        trade_buy: 'Покупка',
        trade_sell: 'Продажа',
        trading_fee: 'Торговая комиссия',
        realized_pnl: 'Результат сделки',
        signal_programmed_profit: 'Программный доход сигнала',
        funding: 'Фандинг',
        margin_lock: 'Резерв маржи',
        margin_release: 'Возврат маржи',
        liquidation: 'Ликвидация',
        transfer_in: 'Перевод на счёт',
        transfer_out: 'Перевод со счёта',
        copy_allocation: 'Выделено на копирование',
        copy_release: 'Возврат из копирования',
        copy_commission: 'Комиссия копи-трейдинга',
        referral: 'Реферальное начисление',
        adjustment: 'Корректировка',
      },
    },
  },

  trade: {
    title: 'Торговля',
    menu: {
      account: 'Счёт',
      trading: 'Торговля',
      more: 'Ещё',
    },
    long: 'Лонг',
    short: 'Шорт',
    buy: 'Купить',
    sell: 'Продать',
    marketOrder: 'Рыночный',
    limitOrder: 'Лимитный',
    price: 'Цена',
    marketPrice: 'По рынку',
    quantity: 'Количество',
    leverage: 'Плечо',
    takeProfitStopLoss: 'Тейк-профит / стоп-лосс',
    positions: 'Позиции',
    orders: 'Ордера',
    history: 'История',
    copyTrading: 'Копитрейдинг',
    unrealizedPnl: 'Нереал. P&L',
    unrealizedPnlShort: 'P&L',
    margin: 'Маржа',
    entry: 'Вход',
    size: 'Размер',
    liquidation: 'Ликвидация',
    close: 'Закрыть',
    orderBook: 'Стакан',
    noPositions: 'Разместите ордер выше, и позиция появится здесь.',
    balance: 'Баланс',
    perpetual: 'Бессрочный',
    perpShort: 'Perp',
    fundingIn: (time: string) => `через ${time}`,
    position: 'Позиция',
    closeAtMarket: 'Закрыть по рынку',
    keepPosition: 'Оставить позицию',
    placeBuyOrder: 'Разместить ордер на покупку',
    placeSellOrder: 'Разместить ордер на продажу',
    placeLongOrder: 'Разместить ордер в лонг',
    placeShortOrder: 'Разместить ордер в шорт',
    isolatedMargin: 'Изолированная маржа',
    crossMargin: 'Кросс-маржа',
    markShort: 'Марк-цена',
    liqPrice: 'Цена ликв.',
    noPositionsTitle: 'Открытых позиций нет',
    noOrdersTitle: 'Активных ордеров нет',
    noHistoryTitle: 'Сделок пока нет',
    pair: 'Пара',
    selectPair: 'Выберите пару',
    sideLabel: 'Направление',
    orderType: 'Тип ордера',
    sizeShare: 'Доля доступного',
    quantityShort: 'Кол-во',
    takeProfit: 'Тейк-профит',
    stopLoss: 'Стоп-лосс',
    tabsLabel: 'Мои сделки',
    openChart: 'График',
    openLong: (symbol: string) => `Лонг ${symbol}`,
    openShort: (symbol: string) => `Шорт ${symbol}`,
    buyAsset: (symbol: string) => `Купить ${symbol}`,
    sellAsset: (symbol: string) => `Продать ${symbol}`,
    enterQuantity: 'Укажите количество',
    insufficient: 'Недостаточно средств на счёте',
    belowMinNotional: (amount: string, asset: string) =>
      `Минимальный объём ордера — ${amount} ${asset}`,
    bookEmpty: 'Заявок в стакане пока нет',
    bookUnavailable: 'Стакан недоступен',
    tradingUnavailable:
      'Торговый сервис не отвечает — отправить ордер сейчас нельзя. Балансы, пополнения и выводы работают как обычно.',
    marketsFailed: 'Не удалось загрузить список пар',
    noMarkets: 'Торговых пар пока нет — они появятся по мере запуска рынков.',
    confirmTitle: 'Проверьте ордер',
    confirmSubtitle: 'Исполненный ордер отменить нельзя.',
    marginRequired: 'Используемая маржа',
    cost: 'К оплате',
    liquidationEstimate: 'Расчётная ликвидация',
    submitted: 'Ордер отправлен',
    submitFailed: 'Не удалось отправить ордер',
    futuresSoon: 'Фьючерсы временно недоступны. Спотовая торговля работает.',
    marginMode: 'Маржа',
    cross: 'Кросс',
    isolated: 'Изолированная',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? 'Кросс: позицию поддерживает весь фьючерсный баланс'
        : 'Изолированная: рискует только маржа этой позиции',
    reduceOnly: 'Только закрытие',
    futuresAccount: 'Фьючерсный счёт',
    equity: 'Капитал',
    marginUsed: 'В марже',
    marginRatio: 'Уровень риска',
    funding: 'Фандинг',
    fundingRate: 'Ставка за 8 ч',
    nextFunding: 'Следующее',
    fundingPaid: 'Финансирование',
    protection: 'Защита',
    editProtection: 'TP / SL',
    protectionSheetTitle: 'Тейк-профит и стоп-лосс',
    protectionSheetSubtitle: 'Движок закроет позицию по рыночной цене, как только цена коснётся уровня.',
    clearLevel: 'Без уровня',
    addMargin: 'Добавить маржу',
    removeMargin: 'Убрать маржу',
    marginDelta: 'Сумма маржи',
    marginDeltaHint: 'Изолированная позиция: больше маржи — дальше ликвидация',
    addMarginKyc: 'Добавить маржу можно после одобрения документов. Убрать маржу, изменить тейк-профит и стоп-лосс или закрыть позицию можно уже сейчас.',
    saveProtection: 'Сохранить',
    protectionSaved: 'Защита обновлена',
    protectionFailed: 'Не удалось обновить позицию',
    protectionInvalid: 'Уровень стоит не с той стороны от цены входа',
    leverageTooHigh: 'Плечо выше допустимого для этой пары',
    leverageTier: (cap: number, notional: string) => `Для объёма ${notional} USDT плечо до ${cap}×`,
    futuresDisabled: 'Фьючерсные ордера временно не принимаются',
    liquidated: 'Ликвидирована',
    restingToast: (symbol: string, price: string) => `Лимитный ордер ${symbol} по ${price} размещён и ждёт цены`,
    cancelledToast: 'Ордер отменён, средства возвращены',
    costSell: 'К получению',
    costBasis: 'Стоимость',
    enterProtection: 'Уровень тейк-профита или стоп-лосса указан неверно',
    leverageLocked: (leverage: number) => `Плечо открытой позиции — ${leverage}×. Изменить его можно, только закрыв позицию`,
    marginReleased: 'Вернётся маржи',
    realizedEstimate: 'Расчётный результат',
    addsPosition: (quantity: string, asset: string) => `Добавит ${quantity} ${asset} к открытой позиции`,
    reducesPosition: (quantity: string, asset: string) => `Уменьшит открытую позицию на ${quantity} ${asset}`,
    closesPosition: (quantity: string, asset: string) => `Закроет открытую позицию ${quantity} ${asset}`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `Закроет позицию ${closeQuantity} ${asset} и откроет ${openQuantity} ${asset} в другую сторону`,
    byAccount: 'по всему счёту',
    cancelledToastPlain: 'Ордер отменён',
    fillBuy: 'Покупка',
    fillSell: 'Продажа',
    marginFloor: (floor: string) => `Маржу нельзя опустить ниже начальной — ${floor} USDT`,
    marginRemovable: (amount: string) => `Можно убрать до ${amount} USDT`,
    enterPrice: 'Укажите цену лимитного ордера',
    markPrice: 'Марк-цена',
    tpShort: 'TP',
    slShort: 'SL',
    priceStale: 'Цена устарела — фид котировок отстаёт. Попробуйте через несколько секунд.',
    marketDisabled: 'Торги по этой паре приостановлены',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `Исполнено: ${side.toLocaleLowerCase('ru-RU')} ${quantity} ${asset} по ${price}`,
    closedToast: (symbol: string, pnl: string) => `${symbol} закрыта, результат ${pnl} USDT`,
    positionsFailed: 'Не удалось загрузить позиции',
    noOrders: 'Лимитный ордер будет ждать здесь, пока не исполнится.',
    ordersFailed: 'Не удалось загрузить ордера',
    noHistory: 'Первая исполненная сделка появится в этом списке.',
    historyFailed: 'Не удалось загрузить историю сделок',
    cancelOrder: 'Отменить',
    cancelFailed: 'Не удалось отменить ордер',
    closeFailed: 'Не удалось закрыть позицию',
    closeConfirmTitle: 'Закрыть позицию?',
    closeConfirmSubtitle:
      'Позиция закроется по рыночной цене, и текущий результат станет окончательным. Отменить это нельзя.',
  },

  chart: {
    title: 'График',
    interval: 'Интервал',
    chartType: 'Тип графика',
    candles: 'Свечи',
    line: 'Линия',
    high24h: 'Макс. 24ч',
    low24h: 'Мин. 24ч',
    volume24h: 'Объём 24ч',
    empty: 'График появится, когда по этой паре накопятся данные',
    failed: 'Не удалось загрузить график',
    unknownPair: 'Такой пары нет — выберите её на вкладке «Рынки»',
    toMarkets: 'К рынкам',
  },

  security: {
    title: 'Безопасность',
    subtitle: 'Второй фактор защищает вход и вывод средств',
    statusOn: 'Включён',
    statusOff: 'Не включён',
    explain:
      'Код из приложения-аутентификатора спрашивается при входе и при каждой заявке на вывод. Без него аккаунт защищён только паролем.',
    setup: 'Подключить аутентификатор',
    step1: 'Отсканируйте код в Google Authenticator, 1Password или Aegis',
    secretLabel: 'Или введите секрет вручную',
    step2: 'Введите код из приложения',
    enable: 'Включить',
    enabled: 'Второй фактор включён',
    disableTitle: 'Выключить второй фактор',
    disableHint: 'Введите текущий код, чтобы подтвердить, что телефон у вас',
    disable: 'Выключить',
    disabled: 'Второй фактор выключен',
    codeInvalid: 'Код не подошёл. Проверьте время на телефоне и введите новый',
    failed: 'Не удалось изменить настройки безопасности',
    withdrawalCode: 'Код подтверждения',
    withdrawalCodeHint: 'Вывод защищён вторым фактором: введите код из приложения',
    recoveryTitle: 'Коды восстановления',
    recoveryIntro: 'Сохраните эти коды в надёжном месте. Каждый открывает вход или вывод один раз, если телефон недоступен. Больше они не показываются.',
    recoveryCopy: 'Скопировать коды',
    recoveryCopied: 'Коды скопированы',
    recoverySaved: 'Я сохранил коды',
    recoveryLeft: (count: number): string => `Кодов восстановления осталось: ${count}`,
    recoveryRegenerate: 'Новые коды восстановления',
    recoveryRegenerateHint: 'Старые коды перестанут действовать. Введите код из приложения',
    recoveryRegenerated: 'Выпущены новые коды восстановления',
    codeOrRecovery: 'Шесть цифр из приложения или код восстановления',
  },

  addressBook: {
    title: 'Адреса для вывода',
    hint: 'Новый адрес начинает работать после подтверждения по ссылке из письма. Так чужая сессия не сможет увести средства на свой адрес.',
    add: 'Добавить адрес',
    address: 'Адрес',
    label: 'Название',
    labelPlaceholder: 'Например, Ledger',
    send: 'Подтвердить по почте',
    saved: 'Сохранённые адреса',
    empty: 'Пока ни одного адреса',
    emptyBody: 'Добавьте адрес, на который выводите: после подтверждения из письма он выбирается в форме вывода одним касанием.',
    statusConfirmed: 'подтверждён',
    statusPending: 'ждёт подтверждения по почте',
    resend: 'Повторить',
    resendLink: 'Отправить письмо ещё раз',
    remove: 'Удалить адрес',
    removeTitle: 'Удалить адрес?',
    removeHint: 'Выводить на этот адрес станет нельзя, пока вы не добавите и не подтвердите его снова.',
    linkSent: 'Письмо отправлено — откройте ссылку, чтобы подтвердить адрес',
    alreadyConfirmed: 'Этот адрес уже подтверждён',
    invalid: 'Адрес не подходит для выбранной сети',
    confirmed: (short: string): string => `Адрес ${short} подтверждён`,
    confirmFailed: 'Ссылка недействительна или устарела',
  },

  sessions: {
    title: 'Сессии и устройства',
    subtitle: 'Где выполнен вход в ваш аккаунт',
    current: 'Это устройство',
    started: 'Вход',
    lastSeen: 'Активность',
    end: 'Завершить',
    endOthers: 'Завершить все другие сессии',
    ended: 'Сессия завершена',
    endedOthers: (count: number): string => `Завершено сессий: ${count}`,
    empty: 'Других активных сессий нет',
    failed: 'Не удалось загрузить сессии',
    hint: 'Завершённая сессия теряет доступ в течение пятнадцати минут: столько живёт уже выданный ключ доступа.',
  },

  profile: {
    title: 'Профиль',
    accountId: 'ID аккаунта',
    idCopied: 'ID скопирован',
    loadFailed: 'Не удалось загрузить профиль',
    partnerProgram: 'Партнёрская программа',
    partnerProgramHint: 'Команда, уровень и начисления',
    securityOn: 'Второй фактор включён',
    securityOff: 'Подключите второй фактор',

    verification: 'Верификация',
    verificationNone: 'Не пройдена',
    verificationPending: 'На проверке',
    verificationInReview: 'На проверке',
    verificationApproved: 'Пройдена',
    verificationRejected: 'Отклонена',
    verificationResubmit: 'Нужны новые документы',
    verificationCta: 'Пройти',
    verificationCtaAgain: 'Отправить заново',
    verificationPrompt: 'Загрузите паспорт — займёт 2 минуты',
    verificationPendingHint: 'Оператор обычно смотрит документы в течение суток.',
    verificationRejectedHint: 'Оператор не принял документы. Заявку можно отправить ещё раз.',
    verificationOpen: 'Открыть заявку',

    withdrawalAddressesHint: 'Адресная книга с подтверждением по почте',
    withdrawalAddresses: 'Адреса для вывода',
    addressesSubtitle: 'Адреса, на которые вы уже выводили средства',
    addressesEmpty: 'Вы ещё не выводили средства. Адрес из первой заявки появится здесь.',
    addressesFailed: 'Не удалось загрузить адреса',
    addressCopied: 'Адрес скопирован',

    security: 'Безопасность',
    accountAccess: 'Почта и пароль',
    sessions: 'Сессии и устройства',
    sessionsHint: 'Где выполнен вход',
    securitySubtitle: 'Вход в аккаунт',
    securityEmailVerified: 'Почта подтверждена',
    securityEmailUnverified: 'Почта не подтверждена',
    securityPassword: 'Сменить пароль',
    securityPasswordHint: 'Пришлём на почту ссылку для смены пароля',

    notifications: 'Уведомления',
    pushOn: 'Пуш включён',
    pushOff: 'Пуш выключен',
    pushToggle: 'Push-уведомления',
    pushUnsupported: 'Этот браузер не умеет push-уведомления',
    pushDenied: 'Уведомления запрещены в настройках браузера',
    pushFailed: 'Не удалось включить push-уведомления',
    pushUnavailable: 'Push-уведомления пока не настроены на сервере',


    theme: 'Тема',
    themeHint: 'Чёрная тема — фирменная и экономит батарею на OLED. Светлая — для яркого солнца. «Как в системе» следует за настройкой телефона.',
    themeNames: { system: 'Как в системе', dark: 'Тёмная', light: 'Светлая' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: 'Следует за настройкой устройства',
    language: 'Язык',
    languageHint: 'Язык интерфейса. Суммы остаются в едином формате, даты — по языку.',

    support: 'Поддержка',
    supportTitle: 'Связаться с поддержкой',
    supportChoose: 'Выберите, как вам удобнее',
    supportVia: 'Telegram или почта',
    supportTelegram: 'Написать в Telegram',
    supportEmail: 'Написать на почту',
    about: 'О приложении',
    version: 'Версия',

    signOut: 'Выйти',
    signOutTitle: 'Выйти из аккаунта?',
    signOutBody:
      'Балансы, открытые позиции и история сигналов останутся на месте — вы вернётесь к ним после следующего входа. Другие устройства останутся в аккаунте.',

    kyc: {
      title: 'Верификация',
      intro:
        'Данные видит только оператор проверки. Номер документа хранится зашифрованным и нигде не показывается целиком.',
      loadFailed: 'Не удалось загрузить заявку',

      document: 'Документ',
      documentType: 'Тип документа',
      typePassport: 'Паспорт',
      typeIdCard: 'ID-карта',
      typeDriverLicense: 'Водительские права',
      number: 'Номер документа',
      numberPlaceholder: '4510 123456',
      numberHint: 'Серия и номер — как в документе',
      country: 'Страна выдачи',
      countryPlaceholder: 'Выберите страну',
      issueDate: 'Дата выдачи',
      expiryDate: 'Действителен до',

      personal: 'Владелец документа',
      lastName: 'Фамилия',
      firstName: 'Имя',
      dateOfBirth: 'Дата рождения',

      documents: 'Фото документа',
      docMain: 'Главная страница',
      docMainHint: 'Разворот с фотографией и номером',
      docFront: 'Лицевая сторона',
      docFrontHint: 'Сторона с фотографией и номером',
      choose: 'Выбрать файл',
      replace: 'Заменить',
      remove: 'Убрать',
      fileHint: (size: string) => `JPG, PNG или PDF, до ${size} МБ`,
      megabytes: 'МБ',

      submit: 'Отправить на проверку',
      submitFailed: 'Не удалось отправить заявку. Проверьте данные и попробуйте ещё раз',
      uploadFailed:
        'Данные отправлены, а документы загрузить не удалось. Попробуйте приложить их ещё раз.',
      uploadRetry: 'Загрузить документы',
      submitted: 'Заявка отправлена',
      submittedHint: 'Оператор проверит документы и обновит статус.',

      review: 'Заявка на проверке',
      reviewHint: 'Пока идёт проверка, данные изменить нельзя.',
      approved: 'Верификация пройдена',
      approvedHint: 'Оператор подтвердил документы.',
      rejected: 'Заявка отклонена',
      rejectedHint: 'Исправьте данные и отправьте заявку ещё раз.',
      resubmit: 'Нужны новые документы',
      resubmitHint: 'Загрузите действующий документ и отправьте заявку ещё раз.',
      rejectionReason: 'Причина',
      submittedAt: (date: string) => `Отправлена ${date}`,
      attached: 'Приложенные документы',
      attachedEmpty: 'К заявке не приложено ни одного файла',
      startNew: 'Отправить заново',
      step: (current: number, total: number): string => `Шаг ${current} из ${total}`,

      gate: {

        title: 'Пройдите верификацию, чтобы пользоваться средствами',

        body: 'Пополнять счёт можно уже сейчас. Торговля, сигналы, переводы и вывод откроются после одобрения документов — обычно в течение суток.',

        pending: 'Документы на проверке — доступ откроется после одобрения, обычно в течение суток.',

        action: 'Пройти верификацию',

      },

      kinds: {
        passport_main: 'Главная страница',
        passport_registration: 'Страница с пропиской',
        selfie_with_document: 'Селфи с документом',
        proof_of_address: 'Подтверждение адреса',
      },

      errors: {
        required: 'Заполните поле',
        number: 'Только латинские буквы, цифры, пробел и дефис',
        name: 'Только буквы, пробел, апостроф и дефис',
        country: 'Выберите страну',
        date: 'Укажите дату',
        issueFuture: 'Дата выдачи не может быть в будущем',
        expiryPast: 'Срок действия документа истёк',
        expiryBeforeIssue: 'Дата окончания должна быть позже даты выдачи',
        age: 'Верификация доступна с 18 лет',
        birthAfterIssue: 'Дата рождения должна быть раньше даты выдачи',
        fileType: 'Подойдёт файл JPG, PNG или PDF',
        fileSize: (size: string) => `Файл больше ${size} МБ`,
        documentsRequired: 'Приложите фото или скан документа',
        fileMissing: 'Добавьте этот файл',
      },
    },
  },

  /** The country picker (KYC «Country of issue» and any other country field). */
  countries: {
    search: 'Название или код страны',
    popular: 'Популярные',
    all: 'Все страны',
    nothing: 'Ничего не нашлось. Попробуйте название по-английски или код, например DE',
    found: (count: number): string => `Найдено стран: ${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `Промо-бонус ${amount} USDT разблокирован и зачислен на спот`,
    promoExpired: 'Срок промо-бонуса истёк: условия не выполнены вовремя',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `Приглашённый ${name} квалифицирован: +${signals.text} ${pluralRu(signals, ['бонусный сигнал', 'бонусных сигнала', 'бонусных сигналов'])}`
        : `Приглашённый ${name} квалифицирован`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0)
          ? `+${signals.text} ${pluralRu(signals, ['бонусный сигнал', 'бонусных сигнала', 'бонусных сигналов'])}`
          : null,
        promo === null ? null : `промо-бонус ${promo} USDT заморожен до выполнения условия`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? 'Квалификация по приглашению пройдена' : `Квалификация пройдена: ${perks.join(', ')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? 'Квалификация отозвана: заявка на вывод до конца срока удержания. Бонусные сигналы и промо-бонус отозваны'
          : 'Квалификация отозвана: бонусные сигналы и промо-бонус отозваны'
        : `Квалификация приглашённого ${name} отозвана: бонусные сигналы за него отозваны`,
    referralRewardPaid: (amount: string): string => `Выплачено ${amount} USDT по партнёрской программе`,
    referralRewardRejected: (amount: string): string => `Начисление ${amount} USDT отклонено оператором`,
    partnerLevelReached: (level: string, amount: string): string =>
      `Уровень ${level} достигнут: бонус ${amount} USDT начислен к выплате`,
    title: 'Уведомления',
    settingsTitle: 'Настройки уведомлений',
    settingsHint: 'Отключённая категория остаётся в истории, но не тревожит: без значка и без push. Push работает, когда он включён в профиле.',
    channelInApp: 'Приложение',
    channelPush: 'Push',
    channelTelegram: 'Telegram',
    telegramHint: 'Что бот присылает в чат. Выключенная здесь категория не приходит в Telegram, но остаётся в приложении.',
    telegramLinked: (name: string): string => (name === '' ? 'Подключён Telegram' : `Подключён Telegram: ${name}`),
    telegramLinkedHint: 'Если это были не вы, нажмите, чтобы отключить.',
    telegramUnlinked: (name: string): string => (name === '' ? 'Telegram отключён' : `Telegram отключён: ${name}`),
    telegramUnlinkedHint: 'Этот чат подключили к другому аккаунту.',
    categories: { deposit: 'Депозиты', withdrawal: 'Выводы', copy: 'Копи-трейдинг', trade: 'Сделки и позиции', signal: 'Сигналы', promo: 'Промо-бонусы', referral: 'Партнёрская программа', kyc: 'Верификация', system: 'Системные' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: 'Прочитать все',
    markAllFailed: 'Не удалось отметить уведомления прочитанными',
    loadFailed: 'Не удалось загрузить уведомления',
    empty:
      'Уведомлений пока нет. Здесь появятся зачисления, ответы по заявкам на вывод, сигналы и итоги сделок.',
    unread: 'Непрочитанное',
    today: 'Сегодня',
    yesterday: 'Вчера',
    depositCredited: (amount: string, asset: string) =>
      `Зачислено ${amount} ${asset}`,
    withdrawalApproved: 'Вывод одобрен',
    withdrawalRejected: 'Вывод отклонён',
    withdrawalSent: 'Вывод отправлен в сеть',
    marginCall: 'Маржин-колл',
    positionLiquidated: 'Позиция ликвидирована',
    copyOpened: (master: string) => `${master} открыл позицию`,
    copyClosed: (master: string) => `${master} закрыл позицию`,
    copyStopLoss: 'Сработал стоп-лосс подписки: позиции закрыты, средства возвращены на спотовый счёт',
    copyMasterStopped: 'Мастер остановлен: копирование завершено, средства возвращены на спотовый счёт',
    /** The second line of a copy stop: what went back to spot. */
    copyReleased: (amount: string) => `Вернулось на спот: ${amount}\u00A0USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `Комиссия мастеру ${commission} USDT с прибыли ${profit} USDT` : `Комиссия мастеру ${master}: ${commission} USDT с прибыли ${profit} USDT`,
    signalLive: (title: string, until: string) => `Сигнал «${title}» открыт до ${until}`,
    futuresLiquidated: (symbol: string, price: string) => `Позиция ${symbol} ликвидирована по ${price}`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol}: сработал тейк-профит по ${price}`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol}: сработал стоп-лосс по ${price}`,
    orderFilled: (symbol: string, quantity: string, price: string) => `Лимитный ордер ${symbol} исполнен: ${quantity} по ${price}`,
    orderRejected: (symbol: string) => `Лимитный ордер ${symbol} не исполнен: движок отклонил сделку`,
    orderRejectedFunds: (symbol: string) => `Лимитный ордер ${symbol} не исполнен: на фьючерсном счёте не хватило маржи`,
    orderRejectedLeverage: (symbol: string) => `Лимитный ордер ${symbol} не исполнен: плечо выше допустимого для такого объёма`,
    /** Used when the server sent no title — the type still has to read as a sentence. */
    kycApproved: 'Верификация пройдена',
    kycRejected: 'Верификация отклонена',
    kycResubmit: 'Для верификации нужны новые документы',
    kycExpiring: (date: string) => `Срок документа истекает ${date} — обновите верификацию`,
    kycExpired: 'Срок документа истёк — загрузите действующий документ',
    signalClosed: (title: string) => `Сигнал закрыт: ${title}`,
    systemAlert: (check: string) => `Сбой: ${check}`,
    systemRecovered: (check: string) => `Восстановлено: ${check}`,
    /** The Notification Row's one-line title: the event, with the particulars in the body. */
    heads: {
      signalLive: 'Сигнал открыт',
      signalClosed: 'Сигнал закрыт',
      orderFilled: 'Лимитный ордер исполнен',
      orderRejected: 'Лимитный ордер не исполнен',
      takeProfit: 'Сработал тейк-профит',
      stopLoss: 'Сработал стоп-лосс',
      promoUnlocked: 'Промо-бонус разблокирован',
      promoExpired: 'Срок промо-бонуса истёк',
      referralQualified: 'Приглашённый квалифицирован',
      referralWelcome: 'Квалификация пройдена',
      referralRevoked: 'Квалификация отозвана',
      rewardPaid: 'Вознаграждение выплачено',
      rewardRejected: 'Начисление отклонено',
      partnerLevelReached: 'Новый партнёрский уровень',
      kycExpiring: 'Срок документа истекает',
      kycExpired: 'Срок документа истёк',
      systemAlert: 'Сбой',
      systemRecovered: 'Восстановлено',
    },
    fallback: {
      deposit: 'Пополнение зачислено',
      withdrawal: 'Обновление по заявке на вывод',
      trade: 'Событие по позиции',
      copy: 'Событие копи-трейдинга',
      referral: 'Событие партнёрской программы',
      system: 'Системное уведомление',
    },
  },

  referrals: {
    title: 'Партнёрская программа',
    rewardKind: {
      fee_rebate: 'Доля комиссии',
      signal_grant: 'Бонусные сигналы',
      promotional_credit: 'Промобонус',
      deposit_bonus: 'Процент с депозита',
      welcome_bonus: 'Приветственный процент',
      level_bonus: 'Бонус за уровень',
    },
    rewardStatus: {
      pending_review: 'На проверке',
      approved: 'Одобрено',
      paid: 'Выплачено',
      rejected: 'Отклонено',
      reversed: 'Отменено',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: 'Заморожен',
      unlocked: 'Зачислен',
      expired: 'Сгорел',
      revoked: 'Отозван',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: 'Мембер',
      bronze: 'Бронза',
      silver: 'Серебро',
      gold: 'Золото',
      platinum: 'Платина',
      ambassador: 'Амбассадор',
    } as Record<string, string>,
    signalUnit: 'сигн.',
    membersFrom: (count: Count): string => `от ${count.text} ${pluralRu(count, ['участника', 'участников', 'участников'])}`,
    baseLevel: 'стартовый уровень',
    depositFrom: (amount: string): string => `депозит от ${amount}`,
    noWeeklySignals: 'без сигналов',
    perWeek: (count: Count): string => `${count.text} сигн./нед.`,
    current: 'сейчас',
    copyFailed: 'Не удалось скопировать',
    inviteText: 'Присоединяйтесь к 19CRT по моему коду',
    linkCopied: 'Ссылка скопирована',
    codeCopied: 'Код скопирован',
    loadError: 'Не удалось загрузить партнёрскую программу',
    loadErrorHint: 'Проверьте соединение и попробуйте ещё раз',
    retry: 'Повторить',
    disabled:
      'Партнёрская программа временно приостановлена. Ваша статистика и начисления сохранены.',
    yourCode: 'Ваш код',
    codeHint: 'Приглашайте напрямую — условия видны до регистрации',
    openQr: 'Открыть QR-код приглашения',
    qrCode: 'QR-код',
    share: 'Поделиться',
    copy: 'Копировать',
    yourLevel: 'Ваш уровень',
    participant: 'Участник',
    allLevels: 'Все уровни',
    progressLabel: 'Прогресс до следующего уровня',
    /** Follows a styled count in the progress meta: « 1 квалифицирован », « 5 квалифицировано ». */
    qualified: (count: Count): string => pluralRu(count, ['квалифицирован', 'квалифицировано', 'квалифицировано']),
    nextLevel: (remaining: Count, level: string): string => `ещё ${remaining.text} до уровня «${level}»`,
    maxLevel: 'максимальный уровень',
    invited: 'Приглашено',
    confirmed: 'Подтверждено',
    paidUsdt: 'Выплачено, USDT',
    signalsReceived: 'Сигналов получено',
    transparentTitle: 'Прозрачное начисление',
    transparentHint:
      'Вознаграждение считается только от фактически удержанной торговой комиссии. Депозит приглашённого не перераспределяется.',
    rewards: 'Начисления',
    rewardHistory: 'История вознаграждений',
    underReview: 'На проверке',
    emptyRewardsTitle: 'Начислений пока нет',
    emptyRewards: 'Начисления появятся после первой квалифицированной активности команды.',
    levelsRules: 'Уровни и правила',
    levelsRulesHint: 'Команда, доля комиссии и сигналы',
    inviteTitle: 'Приглашение в 19CRT',
    inviteSubtitle: 'Код привяжется при регистрации',
    copyLink: 'Копировать ссылку',
    levelsTitle: 'Партнёрские уровни',
    levelsSubtitle: 'Учитываются только квалифицированные прямые участники',
    loadLevelsError: 'Не удалось загрузить уровни',
    levelTeam: 'Уровень и команда',
    feeSignals: 'Комиссия · сигналы',
    depositTiersTitle: 'Проценты по сумме депозита',
    depositTiersHint: 'Чем больше первый депозит, тем больше процент обеим сторонам',
    tierDeposit: 'Депозит',
    tierReferrer: 'Вам',
    tierWelcome: 'Новичку',
    tierFrom: (amount: string): string => `от ${amount}`,
    levelBonus: 'Бонус за уровень',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: 'Правила программы',
      qualification: (amount: string, asset: string): string =>
        `Квалификация — первый зачисленный депозит от ${amount} ${asset} одной суммой.`,
      qualificationKyc: 'Нужна пройденная верификация.',
      welcome: (signals: Count, days: Count): string =>
        `Новичку — ${signals.text} ${pluralRu(signals, ['бонусный сигнал', 'бонусных сигнала', 'бонусных сигналов'])} на ${days.text} ${pluralRu(days, ['день', 'дня', 'дней'])}.`,
      referrer: (signals: Count, days: Count): string =>
        `Партнёру — ${signals.text} ${pluralRu(signals, ['бонусный сигнал', 'бонусных сигнала', 'бонусных сигналов'])} на ${days.text} ${pluralRu(days, ['день', 'дня', 'дней'])} за каждого квалифицированного приглашённого.`,
      promo: (amount: string, required: string, days: Count): string =>
        `Промо-бонус ${amount} USDT замораживается и становится деньгами на споте, когда оборот сделок новичка достигает ${required} USDT за ${days.text} ${pluralRu(days, ['день', 'дня', 'дней'])}; иначе сгорает.`,
      hold: (days: Count): string =>
        `Любая заявка на вывод в течение ${days.text} ${pluralRu(days, ['дня', 'дней', 'дней'])} после квалификации отменяет её вместе с бонусными сигналами и промо-бонусом.`,
      feeShare:
        'Доля комиссии по уровню считается только от торговой комиссии, фактически удержанной с приглашённого. Денежные начисления проверяет оператор и выплачивает отдельно — на баланс они не зачисляются.',
      secondLevel: (rate: string): string => `Партнёр партнёра получает ${rate} той же комиссии.`,
      depositTiers:
        'Процент приглашающему и новичку зависит от суммы первого депозита — по таблице в этом окне.',
      balanceShare: (percent: string): string =>
        `Депозит должен быть не меньше ${percent} % баланса приглашающего на момент зачисления.`,
      topup: (days: Count): string =>
        `Повторный депозит в течение ${days.text} ${pluralRu(days, ['дня', 'дней', 'дней'])} после первого складывается с ним, пара пересчитывается по таблице, разница доплачивается — один раз.`,
      premiumSignal: (count: string): string =>
        `Постоянный премиум-сигнал открывается, когда квалифицированных приглашённых становится ${count} или больше.`,
      antiabuse:
        'Совпадение устройства или адреса с партнёром или его командой отправляет квалификацию на проверку оператора.',
      paused: 'Программа приостановлена оператором: новые квалификации и начисления не создаются.',
    },
    disclaimer:
      'Партнёрские вознаграждения — не гарантированный доход. Регистрация, депозиты и торговый оборот сами по себе не создают денежной выплаты.',
    team: 'Команда',
    teamStatus: {
      pending: 'ожидает депозит',
      flagged: 'на проверке у оператора',
      qualified: 'квалифицирован',
      revoked: 'квалификация отозвана',
    },
    joined: (date: string): string => `с ${date}`,
    showAll: (count: Count): string => `Показать всех (${count.text})`,
    showLess: 'Свернуть',
    promoTitle: 'Промо-бонус',
    promoLocked: (amount: string): string => `${amount} USDT заморожено`,
    promoUnlocked: (amount: string): string => `${amount} USDT зачислено на спот`,
    promoExpired: 'Срок истёк, бонус сгорел',
    promoRevoked: 'Бонус отозван',
    promoCondition: (traded: string, required: string): string => `Оборот ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `до ${date}`,
    promoHint: 'Бонус становится деньгами, когда оборот по сделкам достигает условия до срока.',
  },

  signals: {
    title: 'Сигналы',
    tabs: { today: 'Сегодня', history: 'История' },
    tabsLabel: 'Раздел сигналов',
    access: {
      public: 'Открытый',
      base: 'Базовый',
      premium: 'Премиум',
      bonus: 'Бонусный',
      agent: 'Партнёрский',
    },
    status: {
      scheduled: 'Ожидается',
      available: 'Доступен',
      executed: 'Исполнен',
      expired: 'Истёк',
      cancelled: 'Отменён',
    },
    side: { buy: 'Покупка', sell: 'Продажа' },
    direction: { buy: 'Лонг · покупка', sell: 'Шорт · продажа' },
    positionSide: { buy: 'Лонг', sell: 'Шорт' },
    localTime: 'по местному времени',
    localClock: 'Местное время',
    timeZone: (offset: string) => `Часовой пояс ${offset}`,
    reason: {
      unavailable: 'Сигнал сейчас недоступен',
      already: 'Вы уже исполнили этот сигнал',
      expired: 'Время исполнения сигнала истекло',
      notReleased: 'Сигнал ещё не открыт',
      cancelled: 'Сигнал отменён',
      balance: 'Недостаточно средств на спотовом счёте',
      balanceFutures: 'Недостаточно USDT на спотовом и фьючерсном счетах вместе',
      positionConflict: 'У вас открыта позиция по этому рынку в обратную сторону\u00A0— закройте её, чтобы исполнить сигнал',
      price: 'Цена по этому рынку сейчас недоступна\u00A0— попробуйте через несколько секунд',
      access: 'Для этого сигнала нужен другой уровень доступа',
      kyc: 'Сначала пройдите верификацию\u00A0— после одобрения документов сигнал можно исполнить',
      minimum: 'Сигнал берёт долю вашего общего баланса, и она меньше минимальной суммы. Пополните баланс, чтобы его исполнить',
      maximum: 'Сумма выше допустимой для этого сигнала',
      belowMinimum: (amount: string) => `Сигнал берёт долю вашего общего баланса, и она меньше минимума\u00A0— ${amount}\u00A0USDT. Пополните баланс, чтобы его исполнить`,
      aboveMaximum: (amount: string) => `Сумма выше допустимой для этого сигнала\u00A0— максимум ${amount}\u00A0USDT`,
      notFound: 'Сигнал с таким кодом не найден',
      generic: 'Не удалось выполнить действие. Проверьте условия и попробуйте ещё раз',
      network: 'Не удалось связаться с сервером. Проверьте соединение и попробуйте ещё раз',
    },
    enterCode: 'Ввести код',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: 'Введите код из канала — перед подтверждением вы увидите направление, сумму и условия', executed: 'Итог и фактические параметры — во вкладке «История»', scheduled: (time: string) => `Код появится в канале в ${time}` },
    executing: 'Исполняется',
    notExecuted: 'Не исполнен',
    executedLabel: 'Исполнено',
    loadSignalsError: 'Не удалось загрузить сигналы',
    noSignalsToday: 'Сегодня сигналов нет',
    loadErrorHint: 'Проверьте соединение и попробуйте ещё раз',
    retry: 'Повторить',
    signalAvailable: 'Сигнал доступен',
    nextSignal: 'Следующий сигнал',
    windowOpenUntil: (time: string) => `Окно открыто до ${time}`,
    opensAt: (time: string) => `Откроется в ${time}`,
    execute: 'Исполнить',
    doneToday: 'На сегодня всё',
    newBySchedule: 'Новые сигналы появятся по расписанию',
    codeOverline: 'Код сигнала',
    checkTerms: 'Проверить условия',
    /**
     * Where codes come from, and never how long one works: the client is not
     * told the window (30.09.2026). The rule stays on the server: five minutes
     * by default (`signals.code_ttl_minutes`), a signal may set its own up to a day.
     */
    codeHint: 'Код публикуется в нашем Telegram-канале\u00A0— введите его сразу, как увидите.',
    openChannel: 'Открыть канал',
    /** The words in `codeHint` and `home.signalsNone` that link to the channel. */
    channelLink: 'Telegram-канале',
    panelTitle: 'Код сделки',
    dealsTitle: 'Сделки по кодам',
    dealsEmpty: 'Сделок по кодам пока нет',
    dealsEmptyHint: 'Введите код из канала — сделка и её итог появятся здесь',
    codePlaceholder: 'Например, 1QF9ET7RQ',
    codeRequired: 'Введите код из канала',
    codeLabel: 'Код сигнала',
    check: 'Проверить',
    schedule: 'Расписание',
    todaySignals: 'Сигналы сегодня',
    noSignals: 'Сейчас сигналов нет. Новые коды выходят по расписанию в нашем Telegram-канале.',
    riskDisclosure:
      'Сигнал — торговая идея, а не гарантированный результат. До подтверждения вы увидите направление, сумму, плечо, комиссию и условия закрытия.',
    loadHistoryError: 'Не удалось загрузить историю сигналов',
    historyEmpty: 'История пока пуста',
    historyEmptyHint: 'Здесь появятся все исполненные сигналы и их фактические параметры.',
    toSignals: 'К сигналам',
    allExecutions: 'Все исполнения',
    signalAccepted: 'Сигнал принят',
    confirmation: 'Подтверждение сигнала',
    sentToTrading: 'Ордер передан в торговую систему',
    checkBeforeExecute: 'Проверьте условия перед исполнением',
    acceptedHint: 'Повторное нажатие не создаст второй ордер.',
    ready: 'Готово',
    account: 'Счёт',
    spot: 'Спотовый',
    futures: 'Фьючерсный',
    /** The market kind next to the pair in the signal flow: « XAU/USDT · фьючерс ». */
    perpetualMarket: 'фьючерс',
    validUntil: 'Срок действия',
    signalName: 'Сигнал',
    /** The value next to `validUntil`: a clock time with the preposition it needs. */
    until: (time: string) => `до ${time}`,
    /** The ticking part of the same value, before `until`: « ещё 04:12 », the countdown already formatted. */
    timeLeft: (countdown: string) => `ещё ${countdown}`,
    priceGuide: 'Ориентир цены',
    entryGuide: 'Ориентир входа',
    marketPrice: 'Рыночная',
    stopLoss: 'Стоп-лосс',
    takeProfit: 'Тейк-профит',
    autoClose: 'Закроется сама',
    inDuration: (duration: string) => `через ${duration}`,
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days}\u00A0дн.` : null, hours > 0 ? `${hours}\u00A0ч` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes}\u00A0мин` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: 'Уже исполнен',
    executionUnavailable: 'Исполнение недоступно',
    executionAmount: 'Сумма исполнения',
    marginAmount: 'Маржа',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `${share} общего баланса · ${total}\u00A0USDT`,
    estimatedFee: 'Расчётная комиссия',
    totalDebit: 'Всего к списанию',
    positionSize: 'Размер позиции',
    willSell: 'Будет продано',
    fundingNote: (amount: string) =>
      `Со спотового счёта на фьючерсный будет переведено ${amount}\u00A0USDT под эту сделку. Когда позиция закроется, эта сумма с учётом итога вернётся на спотовый счёт.`,
    executionRisk:
      'Сигнал — торговая идея, а не гарантированный результат. Цена исполнения может отличаться от расчётной, сделка может принести убыток. Подтверждайте только понятный вам риск.',
    executionRiskFutures:
      'Сигнал — торговая идея, а не гарантированный результат. Цена входа может отличаться от расчётной, а плечо увеличивает и прибыль, и убыток — вплоть до ликвидации позиции. Подтверждайте только понятный вам риск.',
    retryExecution: 'Повторить исполнение',
    outcomeLabel: 'итог, USDT',
    outcomeOpen: 'в работе',
    outcome: { take_profit: 'закрыт по цели', stop_loss: 'закрыт по стопу', time: 'закрыт по времени', manual: 'закрыт вручную', liquidation: 'закрыт ликвидацией' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: 'По цели', stop_loss: 'По стопу', time: 'По времени', manual: 'Вручную', liquidation: 'Ликвидация' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: 'В работе',
    executeFor: 'Исполнить до',
    openLong: (amount: string) => `Открыть лонг · ${amount}\u00A0USDT`,
    openShort: (amount: string) => `Открыть шорт · ${amount}\u00A0USDT`,
    submittedToast: 'Сигнал принят к исполнению',
    filledOf: (requested: string, filled: string) =>
      `Запрошено ${requested}\u00A0USDT, исполнено ${filled}\u00A0USDT\u00A0— объём округлён до шага лота.`,
    /** Futures: the margin differs from the confirmed figure because the quantity was rounded to the lot step. */
    marginRounded: (requested: string) => `Запрошено ${requested}\u00A0USDT\u00A0— объём округлён до шага лота.`,
    longOpened: (margin: string, leverage: number) => `Лонг открыт: маржа ${margin}\u00A0USDT, плечо ${leverage}×.`,
    shortOpened: (margin: string, leverage: number) => `Шорт открыт: маржа ${margin}\u00A0USDT, плечо ${leverage}×.`,
    fundedFromSpot: (amount: string) => `Со спотового счёта переведено ${amount}\u00A0USDT.`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? 'тейк-профиту' : null, stopLoss ? 'стоп-лоссу' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `Закроется сама через ${duration}`
      const byLevel = `по ${levels.join(duration === null ? ' или ' : ', ')}`
      return duration === null ? `Закроется ${byLevel}` : `Закроется ${byLevel} или через ${duration}`
    },
    openPosition: 'Перейти к позиции',
    returnedToSpot: (amount: string) => `Возвращено на спот: ${amount}\u00A0USDT`,
    positionAccepted: 'Позиция открыта на фьючерсном счёте',
  },

  notFound: {
    title: 'Страница не найдена',
    message: 'Такой страницы здесь нет — ссылка устарела или в адресе опечатка.',
    action: 'На главную',
  },
} as const

export type Dictionary = typeof ru
